import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PrintSingleDocumentModal from '@/components/feature/PrintSingleDocumentModal';
import PrintablePrescription from '@/components/feature/PrintablePrescription';
import PrintableFicheSoin from '@/components/feature/PrintableFicheSoin';
import PrintableBonLaboratoire from '@/components/feature/PrintableBonLaboratoire';
import PlaintesSection from '@/pages/medecin/suite-soins/components/PlaintesSection';
import SoinsSection from '@/pages/medecin/suite-soins/components/SoinsSection';
import ComptesRendusSection from '@/pages/medecin/suite-soins/components/ComptesRendusSection';
import OrdonnanceSortieSection, { OrdonnanceSortieData } from '@/pages/medecin/suite-soins/components/OrdonnanceSortieSection';
import ConsultationOverview from '@/pages/medecin/suite-soins/components/ConsultationOverview';
import SuitesAnterieuresSection from '@/pages/medecin/suite-soins/components/SuitesAnterieuresSection';
import BonLaboratoireSection, { BonLaboCreatedData } from '@/pages/medecin/suite-soins/components/BonLaboratoireSection';
import NoteSection from '@/pages/medecin/suite-soins/components/NoteSection';
import ImprimerDocumentsSection from '@/pages/medecin/suite-soins/components/ImprimerDocumentsSection';
import { formatOrdonnanceDocument } from '@/lib/voiesMedicament';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  sexe: string;
  numero_dossier: string;
  groupe_sanguin: string;
  allergies: string;
  telephone: string;
}

interface Suite {
  id: string;
  patient_id: string;
  consultation_id: string | null;
  medecin_initiateur_id: string | null;
  statut: string;
  created_at: string;
  updated_at: string;
}

type PrintDoc =
  | { type: 'fiche-soin' }
  | { type: 'ordonnance'; ordonnance: string; duree?: string; instructions?: string }
  | { type: 'bon-labo'; bon: BonLaboCreatedData };

const labelClass = 'block text-sm font-semibold text-gray-700 mb-2';

// Une suite de soins n'est considérée comme réelle que si elle contient
// au moins une plainte, un soin, un compte rendu ou une ordonnance de sortie.
async function suiteHasContent(suiteId: string): Promise<boolean> {
  const [plaintes, sessions, cr, ord] = await Promise.all([
    supabase.from('plaintes_suivi').select('id', { count: 'exact', head: true }).eq('suite_soin_id', suiteId),
    supabase.from('sessions_soins').select('id', { count: 'exact', head: true }).eq('suite_soin_id', suiteId),
    supabase.from('comptes_rendus_suite').select('id', { count: 'exact', head: true }).eq('suite_soin_id', suiteId),
    supabase.from('prescriptions').select('id', { count: 'exact', head: true }).eq('suite_soin_id', suiteId),
  ]);
  return (plaintes.count ?? 0) + (sessions.count ?? 0) + (cr.count ?? 0) + (ord.count ?? 0) > 0;
}

// Supprime une suite de soins et tout son contenu associé.
async function supprimerSuiteContenu(suiteId: string): Promise<void> {
  await Promise.all([
    supabase.from('plaintes_suivi').delete().eq('suite_soin_id', suiteId),
    supabase.from('sessions_soins').delete().eq('suite_soin_id', suiteId),
    supabase.from('comptes_rendus_suite').delete().eq('suite_soin_id', suiteId),
    supabase.from('prescriptions').delete().eq('suite_soin_id', suiteId),
    supabase.from('notes_suites').delete().eq('suite_soin_id', suiteId),
  ]);
  await supabase.from('suites_soins').delete().eq('id', suiteId);
}

function formatStoredOrdonnance(meds: any): string {
  const list = Array.isArray(meds) ? meds : [];
  return list.map((m: string, i: number) => `${i + 1}. ${m}`).join('\n\n');
}

export default function SuiteSoinsPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const suiteIdParam = searchParams.get('suiteId') || null;
  const patientIdParam = searchParams.get('patientId') || null;

  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [suite, setSuite] = useState<Suite | null>(null);
  const [consultationCtx, setConsultationCtx] = useState<any>(null);
  const [medecinId, setMedecinId] = useState<string>('');
  const [medecinInfo, setMedecinInfo] = useState<any>(null);
  const [plaintes, setPlaintes] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [comptesRendus, setComptesRendus] = useState<any[]>([]);
  const [suitesList, setSuitesList] = useState<any[]>([]);
  const [suitesAnterieures, setSuitesAnterieures] = useState<any[]>([]);
  const [ordonnances, setOrdonnances] = useState<any[]>([]);
  const [bonsLabo, setBonsLabo] = useState<any[]>([]);
  const [printDoc, setPrintDoc] = useState<PrintDoc | null>(null);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [terminating, setTerminating] = useState(false);
  const [creatingSuite, setCreatingSuite] = useState(false);
  const [, setDuplicating] = useState(false);
  const [apercuOuvert, setApercuOuvert] = useState(true);

  const loadMedecinInfo = useCallback(async () => {
    const { data } = await supabase
      .from('medecins')
      .select('*')
      .eq('user_id', user!.id)
      .maybeSingle();
    if (data) {
      setMedecinId(data.id);
      setMedecinInfo(data);
    }
  }, [user]);

  const loadSuiteData = useCallback(async (sid: string) => {
    const { data: suiteData } = await supabase
      .from('suites_soins')
      .select('*')
      .eq('id', sid)
      .maybeSingle();
    if (!suiteData) {
      navigate('/medecin/rendez-vous', { replace: true });
      return;
    }
    setSuite(suiteData);

    const { data: patientData } = await supabase
      .from('patients')
      .select('*')
      .eq('id', suiteData.patient_id)
      .single();
    setPatient(patientData || null);

    if (suiteData.consultation_id) {
      const { data: consData } = await supabase
        .from('consultations')
        .select('*')
        .eq('id', suiteData.consultation_id)
        .maybeSingle();
      setConsultationCtx(consData || null);

      // Charger les suites de soins précédentes liées à la même consultation
      const { data: otherSuites } = await supabase
        .from('suites_soins')
        .select('*')
        .eq('consultation_id', suiteData.consultation_id)
        .neq('id', sid)
        .order('created_at', { ascending: false });

      const suitesWithData = await Promise.all(
        (otherSuites || []).map(async (s) => {
          const [{ data: ses }, { data: pl }, { data: cr }, { data: ord }] = await Promise.all([
            supabase.from('sessions_soins').select('*').eq('suite_soin_id', s.id).order('date_soin', { ascending: true }),
            supabase.from('plaintes_suivi').select('*').eq('suite_soin_id', s.id).order('date_plainte', { ascending: true }),
            supabase.from('comptes_rendus_suite').select('*').eq('suite_soin_id', s.id).order('created_at', { ascending: true }),
            supabase.from('prescriptions').select('id').eq('suite_soin_id', s.id),
          ]);
          return { ...s, sessions: ses || [], plaintes: pl || [], comptesRendus: cr || [], ordonnances: ord || [] };
        })
      );
      setSuitesAnterieures(
        suitesWithData.filter(
          (s) => s.sessions.length > 0 || s.plaintes.length > 0 || s.comptesRendus.length > 0 || s.ordonnances.length > 0
        )
      );
    } else {
      setConsultationCtx(null);
      setSuitesAnterieures([]);
    }

    const [{ data: plaintesData }, { data: sessionsData }, { data: crData }, { data: ordData }] = await Promise.all([
      supabase.from('plaintes_suivi').select('*, medecins(nom, prenom)').eq('suite_soin_id', sid).order('date_plainte', { ascending: true }).order('created_at', { ascending: true }),
      supabase.from('sessions_soins').select('*, medecins(nom, prenom)').eq('suite_soin_id', sid).order('date_soin', { ascending: true }).order('created_at', { ascending: true }),
      supabase.from('comptes_rendus_suite').select('*, medecins(nom, prenom)').eq('suite_soin_id', sid).order('created_at', { ascending: true }),
      supabase.from('prescriptions').select('*').eq('suite_soin_id', sid).eq('type_ordonnance', 'sortie').order('date_prescription', { ascending: false }),
    ]);

    setPlaintes(plaintesData || []);
    setSessions(sessionsData || []);
    setComptesRendus(crData || []);
    setOrdonnances(ordData || []);

    if (suiteData.consultation_id) {
      const { data: bonsData } = await supabase
        .from('bons_laboratoire')
        .select('*')
        .eq('consultation_id', suiteData.consultation_id)
        .order('created_at', { ascending: false });
      setBonsLabo(bonsData || []);
    } else {
      setBonsLabo([]);
    }
  }, [navigate]);

  const loadPatientSuitesList = useCallback(async (pid: string) => {
    const { data: patientData } = await supabase.from('patients').select('*').eq('id', pid).single();
    setPatient(patientData || null);
    const { data } = await supabase
      .from('suites_soins')
      .select('*')
      .eq('patient_id', pid)
      .order('created_at', { ascending: false });
    const all = data || [];
    const nonEmpty = [];
    const uneHeureAvant = Date.now() - 60 * 60 * 1000;
    for (const s of all) {
      // Suite "en cours" non terminée dans l'heure : suppression complète.
      if (s.statut === 'en_cours' && new Date(s.created_at).getTime() < uneHeureAvant) {
        await supprimerSuiteContenu(s.id);
        continue;
      }
      if (await suiteHasContent(s.id)) {
        nonEmpty.push(s);
      } else {
        // Suite vide : on la supprime automatiquement
        await supabase.from('suites_soins').delete().eq('id', s.id);
      }
    }
    setSuitesList(nonEmpty);
  }, []);

  const refreshSuiteData = useCallback(() => {
    if (suiteIdParam) loadSuiteData(suiteIdParam);
  }, [suiteIdParam, loadSuiteData]);

  // Init
  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'medecin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }

    const init = async () => {
      setLoading(true);
      try {
        await loadMedecinInfo();
        if (suiteIdParam) {
          await loadSuiteData(suiteIdParam);
        } else if (patientIdParam) {
          await loadPatientSuitesList(patientIdParam);
        } else {
          navigate('/medecin/rendez-vous', { replace: true });
          return;
        }
      } catch (err) {
        console.error('Erreur init suite de soins:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user, role, suiteIdParam, patientIdParam, navigate, loadMedecinInfo, loadSuiteData, loadPatientSuitesList]);

  const handleTerminerSuite = async () => {
    if (!suite) return;
    if (!window.confirm('Terminer cette suite de soins ? Vous pourrez ensuite en ouvrir une nouvelle si nécessaire.')) return;
    setTerminating(true);
    try {
      const { error } = await supabase
        .from('suites_soins')
        .update({ statut: 'terminee', updated_at: new Date().toISOString() })
        .eq('id', suite.id);
      if (error) throw error;
      setSuite((prev) => (prev ? { ...prev, statut: 'terminee' } : prev));
      setSaveStatus({ type: 'success', msg: 'Suite de soins terminée.' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Erreur lors de la terminaison.' });
    } finally {
      setTerminating(false);
    }
  };

  const handleAnnulerOuverture = async () => {
    if (!suite) return;
    if (!window.confirm('Annuler cette suite de soins ? Elle sera définitivement supprimée avec tout son contenu.')) return;
    setTerminating(true);
    try {
      await supprimerSuiteContenu(suite.id);
      setSaveStatus({ type: 'success', msg: 'Suite de soins annulée.' });
      setTimeout(() => setSaveStatus(null), 3000);
      navigate(`/medecin/suite-soins?patientId=${suite.patient_id}`, { replace: true });
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Erreur lors de l\'annulation.' });
    } finally {
      setTerminating(false);
    }
  };

  const handleNouvelleSuite = async () => {
    if (!patient) return;
    setCreatingSuite(true);
    setSaveStatus(null);
    try {
      // Empêcher l'ouverture d'une nouvelle suite s'il en existe déjà une en cours pour ce patient
      const { data: suiteEnCours } = await supabase
        .from('suites_soins')
        .select('id')
        .eq('patient_id', patient.id)
        .eq('statut', 'en_cours')
        .limit(1)
        .maybeSingle();

      if (suiteEnCours) {
        setSaveStatus({ type: 'error', msg: 'Une suite de soins est déjà en cours pour ce patient. Terminez-la avant d\'en ouvrir une nouvelle.' });
        return;
      }

      const { data: latestCons } = await supabase
        .from('consultations')
        .select('id')
        .eq('patient_id', patient.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const { data: newSuite, error } = await supabase
        .from('suites_soins')
        .insert({
          patient_id: patient.id,
          consultation_id: latestCons?.id || null,
          medecin_initiateur_id: medecinId || null,
          statut: 'en_cours',
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/medecin/suite-soins?suiteId=${newSuite.id}`, { replace: true });
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Erreur lors de la création de la nouvelle suite de soins.' });
    } finally {
      setCreatingSuite(false);
    }
  };

  const handleOrdonnanceCreated = (data: OrdonnanceSortieData) => {
    setPrintDoc({
      type: 'ordonnance',
      ordonnance: formatOrdonnanceDocument(data.medicaments),
      duree: data.duree || undefined,
      instructions: data.instructions || undefined,
    });
    setSaveStatus({ type: 'success', msg: 'Ordonnance de sortie enregistrée.' });
    setTimeout(() => setSaveStatus(null), 3000);
    if (suite) {
      supabase
        .from('prescriptions')
        .select('*')
        .eq('suite_soin_id', suite.id)
        .eq('type_ordonnance', 'sortie')
        .order('date_prescription', { ascending: false })
        .then(({ data }) => {
          if (data) setOrdonnances(data);
        });
    }
  };

  const handleBonCreated = (data: BonLaboCreatedData) => {
    setPrintDoc({ type: 'bon-labo', bon: data });
    setSaveStatus({ type: 'success', msg: 'Bon de laboratoire créé.' });
    setTimeout(() => setSaveStatus(null), 3000);
    if (suite?.consultation_id) {
      supabase
        .from('bons_laboratoire')
        .select('*')
        .eq('consultation_id', suite.consultation_id)
        .order('created_at', { ascending: false })
        .then(({ data: d }) => {
          if (d) setBonsLabo(d);
        });
    }
  };

  const handleDupliquerSuiteAnterieure = async (source: any) => {
    if (!suite) return;
    const soins = source.sessions || [];
    if (soins.length === 0) {
      setSaveStatus({ type: 'error', msg: 'Cette suite ne contient aucun soin à copier.' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    if (!window.confirm('Copier les soins de cette suite dans la fiche de soin actuelle ?')) return;
    setDuplicating(true);
    setSaveStatus(null);
    try {
      const { error } = await supabase.from('sessions_soins').insert(
        soins.map((s: any) => ({
          suite_soin_id: suite.id,
          plainte_id: null,
          description: s.description,
          date_soin: new Date().toISOString().split('T')[0],
          medecin_id: medecinId || s.medecin_id || null,
        }))
      );
      if (error) throw error;
      setSaveStatus({ type: 'success', msg: 'Soins copiés dans la fiche de soin.' });
      setTimeout(() => setSaveStatus(null), 3000);
      refreshSuiteData();
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Erreur lors de la copie des soins.' });
    } finally {
      setDuplicating(false);
    }
  };

  const handleSupprimerSuite = async (suiteId: string) => {
    if (!window.confirm('Supprimer cette suite de soins en cours ? Tout son contenu sera effacé.')) return;
    try {
      await supprimerSuiteContenu(suiteId);
      setSaveStatus({ type: 'success', msg: 'Suite de soins supprimée.' });
      setTimeout(() => setSaveStatus(null), 3000);
      if (patientIdParam) loadPatientSuitesList(patientIdParam);
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Erreur lors de la suppression.' });
    }
  };

  const handleTerminerSuiteById = async (suiteId: string) => {
    if (!window.confirm('Terminer cette suite de soins ?')) return;
    try {
      const { error } = await supabase
        .from('suites_soins')
        .update({ statut: 'terminee', updated_at: new Date().toISOString() })
        .eq('id', suiteId);
      if (error) throw error;
      setSaveStatus({ type: 'success', msg: 'Suite de soins terminée.' });
      setTimeout(() => setSaveStatus(null), 3000);
      if (patientIdParam) loadPatientSuitesList(patientIdParam);
    } catch (err: any) {
      setSaveStatus({ type: 'error', msg: err.message || 'Erreur lors de la terminaison.' });
    }
  };

  const practitionerName = medecinInfo
    ? `${medecinInfo.prenom || ''} ${medecinInfo.nom || ''}`.trim()
    : (user?.user_metadata?.full_name || user?.user_metadata?.prenom || '');
  const practitionerTitle = medecinInfo?.specialite || '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Préparation de la suite de soins...</p>
        </div>
      </div>
    );
  }

  // ---- Mode liste des suites d'un patient ----
  if (!suiteIdParam && patient) {
    const actives = suitesList.filter((s) => s.statut === 'en_cours');
    const terminees = suitesList.filter((s) => s.statut !== 'en_cours');

    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 mb-4 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>Retour
          </button>

          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                                Suites de soins
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {patient.prenom} <span className="uppercase">{patient.nom}</span> • Dossier {patient.numero_dossier || 'N/A'}
              </p>
            </div>
            <button
              onClick={handleNouvelleSuite}
              disabled={creatingSuite}
              className="px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              {creatingSuite ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
              Nouvelle suite de soins
            </button>
          </div>

          {suitesList.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <i className="ri-file-list-3-line text-5xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">Aucune suite de soins pour ce patient.</p>
              <p className="text-sm text-gray-400 mt-1">Vous pouvez en créer une à la fin d'une consultation.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {actives.length > 0 && (
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>En cours ({actives.length})
                </h2>
              )}
              {actives.map((s) => (
                <div
                  key={s.id}
                  className="bg-white rounded-2xl border-2 border-green-200 hover:border-green-400 p-5 transition-all"
                >
                  <div className="flex items-center justify-between gap-3">
                    <Link
                      to={`/medecin/suite-soins?suiteId=${s.id}`}
                      className="flex-1 min-w-0 cursor-pointer"
                    >
                      <p className="font-bold text-gray-900">Suite de soins en cours</p>
                      <p className="text-xs text-gray-500 mt-0.5">Ouverte le {new Date(s.created_at).toLocaleDateString('fr-FR')}</p>
                    </Link>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold whitespace-nowrap">En cours</span>
                      <button
                        onClick={() => handleTerminerSuiteById(s.id)}
                        title="Terminer cette suite de soins"
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-green-600 hover:bg-green-50 hover:text-green-700 cursor-pointer transition-colors"
                      >
                        <i className="ri-check-double-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => handleSupprimerSuite(s.id)}
                        title="Supprimer cette suite de soins"
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 hover:text-red-600 cursor-pointer transition-colors"
                      >
                        <i className="ri-delete-bin-6-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {terminees.length > 0 && (
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide flex items-center gap-2 mt-8">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>Terminées ({terminees.length})
                </h2>
              )}
              {terminees.map((s) => (
                <Link
                  key={s.id}
                  to={`/medecin/suite-soins?suiteId=${s.id}`}
                  className="block bg-white rounded-2xl border border-gray-200 hover:border-gray-300 p-5 transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-gray-700">Suite de soins</p>
                      <p className="text-xs text-gray-500 mt-0.5">Ouverte le {new Date(s.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold whitespace-nowrap">Terminée</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  // ---- Mode détail d'une suite ----
  if (!suite || !patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold">Suite de soins introuvable</p>
        </div>
      </div>
    );
  }

  const age = new Date().getFullYear() - new Date(patient.date_naissance).getFullYear();

  const buildBonPrintData = (bon: any): BonLaboCreatedData => ({
    id: bon.id,
    patientName: `${patient.prenom} ${patient.nom}`,
    practitionerName,
    practitionerTitle,
    analyses: (bon.type_analyse || '').split(' ; ').filter(Boolean),
    description: bon.description || '',
    urgences: !!bon.urgences,
    observations: bon.observations_cliniques || '',
    constantes: {
      taille: bon.taille || '',
      poids: bon.poids || '',
      tension_arterielle: bon.tension_arterielle || '',
      temperature: bon.temperature || '',
      libelle: bon.libelle_constantes || '',
    },
    soins: [],
    patientAge: `${age} ans`,
    patientTelephone: bon.patient_telephone || patient.telephone || '',
    patientProfession: bon.patient_profession || '',
    patientAssure: !!bon.patient_assure,
    medecinTraitant: bon.medecin_traitant || '',
    antecedentsMedicaux: '',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Barre patient + actions */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">
                  Suite de soins — {patient.prenom} {patient.nom}
                </h1>
                <p className="text-sm text-gray-500 truncate">
                  {age} ans • Dossier {patient.numero_dossier || 'N/A'} • {patient.groupe_sanguin || 'GS inconnu'}
                  {consultationCtx?.date_consultation && (
                    <span> • Consultation du {new Date(consultationCtx.date_consultation).toLocaleDateString('fr-FR')}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${suite.statut === 'en_cours' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {suite.statut === 'en_cours' ? 'En cours' : 'Terminée'}
              </span>
              {suite.statut === 'en_cours' && (
                <button
                  onClick={handleAnnulerOuverture}
                  disabled={terminating}
                  title="Annuler l'ouverture de cette suite de soins"
                  className="px-4 py-2.5 border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-close-circle-line"></i>
                  Annuler l'ouverture
                </button>
              )}
              {suite.statut !== 'en_cours' && (
                <button
                  onClick={handleNouvelleSuite}
                  disabled={creatingSuite}
                  className="px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  {creatingSuite ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
                  Nouvelle suite de soins
                </button>
              )}
            </div>
          </div>

          {saveStatus && (
            <div className={`mt-3 p-3 rounded-xl text-sm flex items-center gap-2 ${saveStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              <i className={saveStatus.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
              {saveStatus.msg}
            </div>
          )}

          {/* Contexte consultation */}
          {consultationCtx?.diagnostic && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 flex items-start gap-2">
              <i className="ri-mental-health-line text-amber-600 flex-shrink-0 mt-0.5"></i>
              <span><strong>Diagnostic initial :</strong> {consultationCtx.diagnostic}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Aperçu complet de la consultation source */}
        {consultationCtx && (
          <ConsultationOverview
            consultation={consultationCtx}
            ouvert={apercuOuvert}
            onToggle={() => setApercuOuvert(!apercuOuvert)}
          />
        )}

        {/* Suites de soins précédentes pour cette consultation */}
        <SuitesAnterieuresSection suites={suitesAnterieures} onDuplicate={handleDupliquerSuiteAnterieure} />

        {/* Plaintes & symptômes */}
        <PlaintesSection
          suiteId={suite.id}
          plaintes={plaintes}
          medecinId={medecinId}
          suiteTerminee={suite.statut !== 'en_cours'}
          onRefresh={refreshSuiteData}
        />

        {/* Bon de laboratoire */}
        <BonLaboratoireSection
          patientId={patient.id}
          medecinId={medecinId}
          consultationId={suite.consultation_id}
          suiteTerminee={suite.statut !== 'en_cours'}
          onCreated={handleBonCreated}
        />

        {/* Fiche de soin */}
        <SoinsSection
          suiteId={suite.id}
          sessions={sessions}
          medecinId={medecinId}
          suiteTerminee={suite.statut !== 'en_cours'}
          onRefresh={refreshSuiteData}
        />

        {/* Ordonnance de sortie */}
        <OrdonnanceSortieSection
          patientId={patient.id}
          patientName={`${patient.prenom} ${patient.nom}`}
          medecinId={medecinId}
          suiteId={suite.id}
          onCreated={handleOrdonnanceCreated}
        />

        {/* Compte rendu de relève */}
        <ComptesRendusSection
          suiteId={suite.id}
          comptesRendus={comptesRendus}
          medecinId={medecinId}
          suiteTerminee={suite.statut !== 'en_cours'}
          onRefresh={refreshSuiteData}
        />

        {/* Notes */}
        <NoteSection
          suiteId={suite.id}
          medecinId={medecinId}
          suiteTerminee={suite.statut !== 'en_cours'}
        />

        {/* Imprimer les documents */}
        <ImprimerDocumentsSection
          hasFicheSoin={sessions.length > 0}
          soinsCount={sessions.length}
          ordonnances={ordonnances.map((o) => ({
            id: o.id,
            ordonnance: formatStoredOrdonnance(o.medicaments),
            duree: o.duree_traitement || undefined,
            instructions: o.instructions || undefined,
            date: o.date_prescription,
          }))}
          bonsLabo={bonsLabo.map((b) => ({
            id: b.id,
            analyses: (b.type_analyse || '').split(' ; ').filter(Boolean),
            urgences: !!b.urgences,
            observations: b.observations_cliniques || undefined,
            date: b.date_prescription,
          }))}
          onPrintFicheSoin={() => setPrintDoc({ type: 'fiche-soin' })}
          onPrintOrdonnance={(o) =>
            setPrintDoc({ type: 'ordonnance', ordonnance: o.ordonnance, duree: o.duree, instructions: o.instructions })
          }
          onPrintBon={(b) => {
            const full = bonsLabo.find((x) => x.id === b.id);
            setPrintDoc({ type: 'bon-labo', bon: buildBonPrintData(full || b) });
          }}
        />

        {/* Terminer la suite de soins */}
        {suite.statut === 'en_cours' && (
          <div className="bg-white rounded-2xl border-2 border-green-200 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="font-bold text-gray-900 flex items-center gap-2 justify-center sm:justify-start">
                                Finaliser cette suite
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Une fois terminée, cette suite de soins passera au statut « Terminée » et ne pourra plus être modifiée.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={handleAnnulerOuverture}
                disabled={terminating}
                className="px-5 py-3 border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-close-circle-line"></i>
                Annuler l'ouverture
              </button>
              <button
                onClick={handleTerminerSuite}
                disabled={terminating}
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {terminating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-double-line"></i>}
                Terminer la suite de soins
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Impression fiche de soin */}
      <PrintSingleDocumentModal
        isOpen={printDoc?.type === 'fiche-soin'}
        onClose={() => setPrintDoc(null)}
        documentTitle="Fiche de Soins"
      >
        <PrintableFicheSoin
          patientName={`${patient.prenom} ${patient.nom}`}
          practitionerName={practitionerName}
          practitionerTitle={practitionerTitle}
          date={new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          soins={sessions.map((s) => ({ date: s.date_soin, description: s.description || '' }))}
        />
      </PrintSingleDocumentModal>

      {/* Impression ordonnance de sortie */}
      <PrintSingleDocumentModal
        isOpen={printDoc?.type === 'ordonnance'}
        onClose={() => setPrintDoc(null)}
        documentTitle="Ordonnance de sortie"
      >
        {printDoc?.type === 'ordonnance' && (
          <PrintablePrescription
            patientName={`${patient.prenom} ${patient.nom}`}
            practitionerName={practitionerName}
            practitionerTitle={practitionerTitle}
            date={new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            ordonnance={printDoc.ordonnance}
            dureeTraitement={printDoc.duree}
            instructions={printDoc.instructions}
          />
        )}
      </PrintSingleDocumentModal>

      {/* Impression du bon de laboratoire */}
      <PrintSingleDocumentModal
        isOpen={printDoc?.type === 'bon-labo'}
        onClose={() => setPrintDoc(null)}
        documentTitle="Bon de laboratoire"
      >
        {printDoc?.type === 'bon-labo' && (
          <PrintableBonLaboratoire
            patientName={printDoc.bon.patientName || `${patient.prenom} ${patient.nom}`}
            practitionerName={printDoc.bon.practitionerName || practitionerName}
            practitionerTitle={printDoc.bon.practitionerTitle || practitionerTitle}
            date={new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            analyses={printDoc.bon.analyses}
            description={printDoc.bon.description || undefined}
            observations={printDoc.bon.observations || undefined}
            urgences={printDoc.bon.urgences}
            soins={printDoc.bon.soins || []}
            constantes={printDoc.bon.constantes || { taille: '', poids: '', tension_arterielle: '', temperature: '', libelle: '' }}
            patientAge={printDoc.bon.patientAge || `${age} ans`}
            patientTelephone={printDoc.bon.patientTelephone || ''}
            patientProfession={printDoc.bon.patientProfession || ''}
            patientAssure={printDoc.bon.patientAssure || false}
            medecinTraitant={printDoc.bon.medecinTraitant || ''}
            antecedentsMedicaux={printDoc.bon.antecedentsMedicaux || undefined}
            verificationUrl={`${window.location.origin}/verifier-document?type=bon_laboratoire&id=${printDoc.bon.id}`}
          />
        )}
      </PrintSingleDocumentModal>
    </div>
  );
}